import React from 'react';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, CalendarDays, Mail, Video } from 'lucide-react';
import { motion } from 'framer-motion';

export default function EventsPage() {
  const { language } = useLanguage();

  return (
    <div data-testid="events-page" className="min-h-screen bg-white">
      <SEO path="/events" />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#2D78F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Événements' : 'Events'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/80 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'Portes ouvertes virtuelles et rencontres avec nos professeurs.'
              : 'Virtual open house and meetings with our teachers.'}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Portes ouvertes */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="border-2 border-[#2D78F6]/20 p-10 text-center"
          >
            <div className="w-14 h-14 bg-[#2D78F6]/10 flex items-center justify-center mb-6 mx-auto">
              <CalendarDays className="w-7 h-7 text-[#2D78F6]" />
            </div>
            <h2 className="text-2xl font-semibold text-[#14274B] mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
              {language === 'fr' ? 'Prochaines portes ouvertes virtuelles' : 'Upcoming Virtual Open House'}
            </h2>
            <p className="text-[#475569] mb-6 max-w-xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {language === 'fr'
                ? 'De nouvelles dates de portes ouvertes virtuelles seront annoncées très prochainement. Découvrez OFA France depuis chez vous : présentation du programme, questions-réponses avec notre équipe pédagogique et démonstration de notre plateforme.'
                : 'New virtual open house dates will be announced very soon. Discover OFA France from home: program presentation, Q&A with our academic team and platform demonstration.'}
            </p>
            <div className="bg-[#E7EAEF] p-6 mb-6">
              <p className="text-[#14274B] font-semibold mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Contactez-nous à tout moment' : 'Contact us anytime'}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="mailto:info@ofalycee.fr" className="flex items-center gap-2 text-[#2D78F6] hover:text-[#14274B] transition-colors text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <Mail className="w-4 h-4" />
                  info@ofalycee.fr
                </a>
                <a href="mailto:gguillermin@ofalycee.org" className="flex items-center gap-2 text-[#2D78F6] hover:text-[#14274B] transition-colors text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <Mail className="w-4 h-4" />
                  gguillermin@ofalycee.org
                </a>
              </div>
            </div>
            <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="events-register-btn"
                className="bg-[#2D78F6] text-white hover:bg-[#14274B] px-8"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {language === 'fr' ? 'S\'inscrire dès maintenant' : 'Register now'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#14274B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
            {language === 'fr' ? 'Vous avez des questions ?' : 'Have questions?'}
          </h2>
          <p className="text-white/70 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Notre équipe est disponible à tout moment pour répondre à vos questions.'
              : 'Our team is available at any time to answer your questions.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <a href="mailto:info@ofalycee.fr" className="text-white/80 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
              info@ofalycee.fr
            </a>
            <span className="text-white/30 hidden sm:inline">|</span>
            <a href="mailto:gguillermin@ofalycee.org" className="text-white/80 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
              gguillermin@ofalycee.org
            </a>
          </div>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-[#2D78F6] text-white hover:bg-white hover:text-[#14274B] px-8 py-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {language === 'fr' ? 'S\'inscrire' : 'Register'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
